import { useRef, useEffect } from 'react';
import VerificationInput from 'react-verification-input';
import classnames from 'classnames';
import { VerificationCodeInputProps } from './types';
import s from './index.module.scss';

export const VerificationCodeInput = ({
  label,
  error,
  success,
  value,
  disabled,
  onChange,
  ...rest
}: VerificationCodeInputProps) => {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const fieldsRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const fields = fieldsRef.current;
    if (!fields) return;

    const charClass = s.verificationCodeInput__character;
    if (!charClass) return;

    const cells = fields.querySelectorAll<HTMLElement>(`.${charClass}`);
    const thirdCell = cells[2];
    if (!thirdCell) return;

    const divider = document.createElement('div');
    divider.className = s.verificationCodeInput__divider ?? '';
    divider.setAttribute('aria-hidden', 'true');
    thirdCell.insertAdjacentElement('afterend', divider);
    dividerRef.current = divider;

    return () => {
      divider.remove();
      dividerRef.current = null;
    };
  }, []);

  const handleHiddenInput = () => {
    if (!hiddenInputRef.current || !onChange) return;
    const digits = hiddenInputRef.current.value.replace(/\D/g, '').slice(0, 6);
    if (digits) {
      onChange(digits);
      hiddenInputRef.current.value = '';
    }
  };

  const isComplete = typeof value === 'string' && value.length === 6;

  return (
    <div
      className={classnames(
        s.verificationCodeInput,
        error && s['verificationCodeInput--error'],
        success && !error && s['verificationCodeInput--success'],
        isComplete && s['verificationCodeInput--complete']
      )}
    >
      {label && <span className={s.verificationCodeInput__label}>{label}</span>}

      {/* Hidden input for OTP autofill (iOS/Android) */}
      <input
        ref={hiddenInputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-hidden="true"
        tabIndex={-1}
        disabled={disabled}
        data-testid="otp-autofill-input"
        style={{
          position: 'absolute',
          opacity: 0,
          pointerEvents: 'none',
          width: 0,
          height: 0,
        }}
        onInput={handleHiddenInput}
        onPaste={handleHiddenInput}
      />

      <div ref={fieldsRef} className={s.verificationCodeInput__fields}>
        <VerificationInput
          autoFocus
          length={6}
          validChars="0-9"
          placeholder="•"
          value={value}
          inputProps={{ disabled }}
          classNames={{
            container: s.verificationCodeInput__container,
            character: s.verificationCodeInput__character,
            characterInactive: s.verificationCodeInput__characterInactive,
            characterSelected: s.verificationCodeInput__characterSelected,
            characterFilled: s.verificationCodeInput__characterFilled,
          }}
          onChange={onChange}
          {...rest}
        />
      </div>
    </div>
  );
};